import { WORK_PERMIT_STATUS } from './consts'
import { hasVisibleHistoryText } from './workPermitHistoryCommentDisplay'

/**
 * author: alvis
 * timestamp: 2026-04-22
 * description: `history.detail` key holding merged internal/external HTML for engineer Lifecycle v-html.
 * parameter: n/a
 */
export const WP_LIFECYCLE_COMMENT_HTML_KEY = '_lifecycleCommentHtml'

/**
 * @typedef {Object} LifecycleCommentResult
 * @property {string} comments - Plain lifecycle comment text.
 * @property {Object.<string, string>} [htmlByKey] - Optional safe HTML fragments by detail key.
 */

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Runtime guard for lifecycle comment result output.
 * @param {*} value
 * @returns {value is LifecycleCommentResult}
 */
export function isLifecycleCommentResult (value) {
  return value != null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    typeof value.comments === 'string' &&
    (
      value.htmlByKey == null ||
      (
        typeof value.htmlByKey === 'object' &&
        !Array.isArray(value.htmlByKey) &&
        Object.values(value.htmlByKey).every(v => typeof v === 'string')
      )
    )
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Create the non-null lifecycle comment result contract.
 * `null` remains the sentinel for unchanged rows.
 * @param {Object} value
 * @param {string} [value.comments]
 * @param {Object.<string, string>} [value.htmlByKey]
 * @returns {LifecycleCommentResult}
 */
export function createLifecycleCommentResult ({
  comments = '',
  htmlByKey,
} = {}) {
  const result = { comments: comments == null ? '' : String(comments) }
  if (htmlByKey != null) {
    result.htmlByKey = htmlByKey
  }
  if (!isLifecycleCommentResult(result)) {
    return { comments: '' }
  }
  return result
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Build one labeled HTML row for the Lifecycle modal comment stack.
 * @param {string} label
 * @param {string} bodyEscaped
 * @returns {string}
 */
function lifecycleHtmlLabeledRow (label, bodyEscaped) {
  return (
    '<div class="lifecycle-modal__comment-html-row">' +
    '<span class="lifecycle-modal__comment-html-label"><strong>' +
    escapeHtml(label) +
    '</strong></span>' +
    '<span class="lifecycle-modal__comment-html-text">' +
    bodyEscaped +
    '</span></div>'
  )
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Build one body-only HTML row for the Lifecycle modal comment stack.
 * @param {string} bodyEscaped
 * @returns {string}
 */
function lifecycleHtmlBodyOnlyRow (bodyEscaped) {
  return (
    '<div class="lifecycle-modal__comment-html-row lifecycle-modal__comment-html-row--body-only">' +
    '<span class="lifecycle-modal__comment-html-text">' +
    bodyEscaped +
    '</span></div>'
  )
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Wrap Lifecycle modal comment rows in the stack container.
 * @param {string} rowsHtml
 * @returns {string}
 */
function lifecycleHtmlWrapStack (rowsHtml) {
  return `<div class="lifecycle-modal__comment-html-stack">${rowsHtml}</div>`
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Build a plain and optional HTML lifecycle comment result without labels.
 * @param {string} plain
 * @param {string} htmlKey
 * @returns {LifecycleCommentResult|null}
 */
function buildBodyOnlyCommentResult (plain, htmlKey) {
  if (!hasVisibleHistoryText(plain)) {
    return null
  }
  if (!htmlKey) {
    return createLifecycleCommentResult({ comments: plain })
  }
  return createLifecycleCommentResult({
    comments: plain,
    htmlByKey: {
      [htmlKey]: lifecycleHtmlWrapStack(lifecycleHtmlBodyOnlyRow(escapeHtml(plain))),
    },
  })
}

/**
 * author: alvis
 * timestamp: 2026-05-11
 * description: Exported wrapper for WAPPR resume and other rows that use body-only Lifecycle HTML (no internal/external labels).
 * @param {string} plain
 * @param {string} htmlKey
 * @returns {LifecycleCommentResult|null}
 */
export function buildBodyOnlyLifecycleCommentResult (plain, htmlKey) {
  return buildBodyOnlyCommentResult(plain, htmlKey)
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Check whether APPR/REJECT external-only text should be rendered without labels.
 * @param {string} status
 * @param {string} intTrim
 * @param {string} extTrim
 * @returns {boolean}
 */
function shouldRenderExternalOnlyWithoutLabel (status, intTrim, extTrim) {
  return [WORK_PERMIT_STATUS.APPR, WORK_PERMIT_STATUS.REJECTED].includes(status) &&
    !hasVisibleHistoryText(intTrim) &&
    hasVisibleHistoryText(extTrim)
}

const LIFECYCLE_INTERNAL_EXTERNAL_STATUSES = [
  WORK_PERMIT_STATUS.ACKNOWLEDGED,
  WORK_PERMIT_STATUS.REVIEWED,
  WORK_PERMIT_STATUS.REVIEWED2,
  WORK_PERMIT_STATUS.APPR,
  WORK_PERMIT_STATUS.REJECTED,
]

export { hasVisibleHistoryText }

/**
 * author: alvis
 * timestamp: 2026-04-22
 * description: Build plain-text and optional HTML for Lifecycle comment column; engineers get labeled lines and optional `htmlByKey` for v-html. Returns null when the row is unchanged (e.g. engineer, both fields empty for this status).
 * @param {Object} params
 * @param {string} params.status - Work permit status on the history row.
 * @param {boolean} params.isEngineer - When true, merge internal/external with strong labels; otherwise tenant/contractor rules.
 * @param {string} params.intTrim - Trimmed internal `comments` text.
 * @param {string} params.extTrim - Trimmed external `externalComments` text.
 * @param {string} params.internalLabel - i18n label for internal (plain + HTML line).
 * @param {string} params.externalLabel - i18n label for external.
 * @param {string} [params.htmlKey] - When set, also return `htmlByKey[htmlKey]`; does not write to the item.
 * @param {boolean} [params.bodyOnly=false] - Render the visible comment without internal/external labels.
 * @returns {LifecycleCommentResult|null} Result or null to leave the row unchanged.
 */
export function buildLifecycleTableCommentResult ({
  status,
  isEngineer,
  intTrim,
  extTrim,
  internalLabel,
  externalLabel,
  htmlKey,
  bodyOnly = false,
}) {
  if (!LIFECYCLE_INTERNAL_EXTERNAL_STATUSES.includes(status)) {
    return null
  }
  if (bodyOnly) {
    return buildBodyOnlyCommentResult(hasVisibleHistoryText(intTrim) ? intTrim : extTrim, htmlKey)
  }
  if (isEngineer) {
    /* APPR/REJECT external-only rows keep the Lifecycle modal body unlabeled. */
    const plainParts = []
    const htmlParts = []
    if (hasVisibleHistoryText(intTrim)) {
      plainParts.push(`${internalLabel} ${intTrim}`)
      htmlParts.push(lifecycleHtmlLabeledRow(internalLabel, escapeHtml(intTrim)))
    }
    if (hasVisibleHistoryText(extTrim)) {
      if (shouldRenderExternalOnlyWithoutLabel(status, intTrim, extTrim)) {
        const bodyOnlyResult = buildBodyOnlyCommentResult(extTrim, htmlKey)
        if (bodyOnlyResult) {
          return bodyOnlyResult
        }
      } else {
        plainParts.push(`${externalLabel} ${extTrim}`)
        htmlParts.push(lifecycleHtmlLabeledRow(externalLabel, escapeHtml(extTrim)))
      }
    }
    if (!plainParts.length) {
      return null
    }
    const plain = plainParts.join('\n\n')
    const html = lifecycleHtmlWrapStack(htmlParts.join(''))
    if (!htmlKey) {
      return createLifecycleCommentResult({ comments: plain })
    }
    return createLifecycleCommentResult({ comments: plain, htmlByKey: { [htmlKey]: html } })
  }
  if (hasVisibleHistoryText(extTrim)) {
    return createLifecycleCommentResult({ comments: extTrim })
  }
  if (hasVisibleHistoryText(intTrim)) {
    return createLifecycleCommentResult({ comments: intTrim })
  }
  return createLifecycleCommentResult({ comments: '' })
}

/**
 * author: alvis
 * timestamp: 2026-04-22
 * description: HTML-escape a string for safe v-html (basic entities: `&<>"\'`).
 * @param {*} s - Value to escape; null/undefined become ''.
 * @returns {string}
 */
export function escapeHtml (s) {
  if (s == null) {
    return ''
  }
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}
